import React from 'react';

interface ConfirmUpdateModalProps {
    visible: boolean;
    questionText: string;
    oldAnswer: string;
    newAnswer: string;
    onConfirm: () => void;
    onCancel: () => void;
}

export const ConfirmUpdateModal: React.FC<ConfirmUpdateModalProps> = ({
    visible,
    questionText,
    oldAnswer,
    newAnswer,
    onConfirm,
    onCancel,
}) => {
    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-[999999999999] flex items-center justify-center px-5 bg-black/50">
            <div className="bg-white rounded-lg w-full max-w-lg max-h-[80%] flex flex-col">
                {/* Header */}
                <div className="px-6 pt-6 pb-4 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-900">
                        Change Previous Selection?
                    </h3>
                    <p className="text-sm font-normal text-text-secondary">
                        This will update your answer
                    </p>
                </div>

                {/* Content */}
                <div className="px-6 py-4 overflow-y-auto max-h-96">
                    {/* Question being changed */}
                    <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 mb-3">
                            {questionText}
                        </p>
                        <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center mb-2">
                                <span className="text-xs font-medium text-gray-500 w-16">
                                    From:
                                </span>
                                <span className="text-sm text-gray-900 flex-1 line-clamp-2">
                                    {oldAnswer || 'N/A'}
                                </span>
                            </div>
                            <div className="flex items-center">
                                <span className="text-xs font-medium text-gray-500 w-16">
                                    To:
                                </span>
                                <span className="text-sm text-primary-500 font-medium flex-1 line-clamp-2">
                                    {newAnswer || 'N/A'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Warning message */}
                    <div className="bg-amber-50 border border-warning-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-[18px] w-[18px] text-warning-700" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs text-warning-800 ml-2 flex-1">
                                This change will reset all subsequent answers and proceed to the next question.
                            </span>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="px-6 py-4 border-t flex flex-row justify-between gap-4 border-gray-200">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2.5 h-[44px] rounded-md w-[48%] bg-gray-100 text-base font-medium text-text-200 hover:bg-gray-200 transition-colors"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={onConfirm}
                        className="px-4 py-2.5 h-[44px] rounded-md w-[48%] bg-primary-500 text-white text-base font-medium hover:bg-primary-600 transition-colors"
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};
